import {
  Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis
} from 'recharts';
import styled from 'styled-components';
import {Typography} from '@/components/Typography';
import {GenderAgePopulation} from '@/map/@types';
import {palette} from '@/styles';
import {HighlightBox, SectionTab, SectionTabs} from '../../StyledComponents';
import {CHART_COLORS, GENDER_LABELS} from '../constants';
import {chartWithComma} from '../utils';
import {CustomizedLegend, CustomizedTooltip} from './CustomizedChartItem';

export interface IProps {
  type: string;
  data: GenderAgePopulation[];
  compareList: string[];
  ageIndex: number;
  onAgeChange(ageIndex: number): void;
  ages: string[];
}

export const GenderAgeBarChart = ({
  type,
  data: rawdata,
  compareList,
  ageIndex,
  onAgeChange,
  ages,
}: IProps) => {
  if (!rawdata) {
    return null;
  }

  const getChartData = () => {
    const data: any = [];

    if (!rawdata) {
      return data;
    }

    rawdata.map((area, areaIdx) => {
      if (!area) {
        return undefined;
      }

      Object.entries(area).map(([gender, value], idx) => {
        if (!data[idx]) {
          data.push({
            name: GENDER_LABELS[gender],
          });
        }

        data[idx] = {
          ...data[idx],
          [compareList[areaIdx]]: value[ageIndex],
        };

        return undefined;
      });

      return undefined;
    });

    return data;
  };

  const chartData = getChartData();

  const getMaxData = () => {
    return chartData
      .map((i: any) => {
        const {name, ...rest} = i;

        return {
          name,
          max: Object.entries(rest).reduce(
            (acc, [key, value]) => {
              if ((value as number) > acc.value) {
                acc.name = key;
                acc.value = value as number;
              }

              return acc;
            },
            {name: '', value: 0}
          ),
        };
      })
      .filter((i: any) => i.max.value > 0);
  };

  const maxData = getMaxData();

  return (
    <>
      <SectionTabs>
        {ages.map((i, idx) => (
          <SectionTab
            key={`GenderAgeBarChart-Tab-${idx}`}
            active={idx === ageIndex}
            onClick={() => onAgeChange(idx)}
          >
            {i}
          </SectionTab>
        ))}
      </SectionTabs>
      <ChartWrapper>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} barSize={20}>
            <CartesianGrid
              stroke={palette.gray100}
              vertical={false}
              strokeWidth={1}
            />
            <XAxis
              dataKey="name"
              tickLine={false}
              tick={{
                fill: palette.gray600,
                fontSize: 12,
              }}
            />
            <YAxis
              tickFormatter={chartWithComma}
              interval={0}
              tickCount={5}
              axisLine={false}
              tickLine={false}
              tick={{
                fill: palette.gray400,
                fontSize: 12,
              }}
            />
            <Legend content={<CustomizedLegend />} />
            <Tooltip
              content={<CustomizedTooltip />}
              cursor={{
                fill: '#0000000D',
              }}
            />
            {rawdata.map((_, idx) => (
              <Bar
                key={`GenderAgeBarChart-Bar-${idx}`}
                dataKey={compareList[idx]}
                fill={CHART_COLORS[idx]}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </ChartWrapper>

      <HighlightBox>
        <Typography variant="medium12">
          {!!maxData.length ? (
            <>
              <strong>{ages[ageIndex]}</strong> {type} 수는{' '}
              {maxData.map((i: any, idx: number) => (
                <span key={idx}>
                  {i.name}은{' '}
                  <Typography color={palette.primary500} fontWeight="700">
                    {i.max.name}
                  </Typography>
                  이/가{idx === maxData.length - 1 ? ' 많아요.' : ', '}
                </span>
              ))}
            </>
          ) : (
            `선택하신 지역에 ${ages[ageIndex]} ${type} 데이터가 없습니다.`
          )}
        </Typography>
      </HighlightBox>
    </>
  );
};

const ChartWrapper = styled.div`
  height: 157px;
`;
